import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Play, CheckCircle2, XCircle, Clock, Sparkles, X, ChevronDown, ChevronUp, Award, Edit3, Layers, Folder as FolderIcon, FileText as ResIcon, RotateCcw } from "lucide-react";
import "./AssessmentsPanel.css";

/* ============================================================================
 *  AssessmentsPanel — attached to a Learning Hub collection. Each
 *  assessment is single-attach: scope ∈ {collection, folder, resource}.
 *  Collection scope = tab-wide. Folder scope = shown here with a chip.
 *  Resource scope = shown here AND on the resource viewer banner.
 *
 *  Visual language: dark cockpit cards with glowing teal borders, a
 *  mesh-gradient hero on each card, and animated SVG score rings — the
 *  same aesthetic as Attendance Insights.
 * ============================================================================ */

const QTYPES = [
    { key: "mcq_single",  label: "MCQ · one answer" },
    { key: "mcq_multi",   label: "MCQ · multiple" },
    { key: "true_false",  label: "True / False" },
    { key: "short",       label: "Short answer" },
    { key: "long",        label: "Long answer · AI-graded" },
    { key: "likert",      label: "Likert scale" },
    { key: "scenario",    label: "Scenario · branching" },
    { key: "file",        label: "File upload" },
];

const emptyQuestion = (type = "mcq_single") => {
    const base = { type, prompt: "", points: 1, is_required: true, explanation: "" };
    if (type === "mcq_single") return { ...base, options: ["", ""], correct_answer: 0 };
    if (type === "mcq_multi")  return { ...base, options: ["", ""], correct_answer: [] };
    if (type === "true_false") return { ...base, correct_answer: true };
    if (type === "short")      return { ...base, correct_answer: "" };
    if (type === "long")       return { ...base, points: 5, rubric: "" };
    if (type === "likert")     return { ...base, likert_scale: { min: 1, max: 5, left_label: "Strongly disagree", right_label: "Strongly agree" } };
    if (type === "scenario")   return { ...base, points: 3, scenario_branches: [{ id: `b_${Date.now()}`, prompt: "", choices: [{ id: `c1_${Date.now()}`, label: "", is_correct: true }, { id: `c2_${Date.now()}`, label: "", is_correct: false }] }] };
    if (type === "file")       return { ...base };
    return base;
};

const SCOPE_META = {
    collection: { icon: Layers,     label: "Collection", color: "#2EB4AC" },
    folder:     { icon: FolderIcon, label: "Folder",     color: "#5B7FDE" },
    resource:   { icon: ResIcon,    label: "Resource",   color: "#E7A33E" },
};

const AssessmentsPanel = ({ collection, folders = [], resources = [], folderId = null, userId }) => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [builder, setBuilder] = useState(null);
    const [runner, setRunner] = useState(null);
    const [results, setResults] = useState(null);
    const [scopeFilter, setScopeFilter] = useState("all"); // "all" | "collection" | "folder" | "resource"

    const load = async () => {
        if (!collection?.id) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ collection_id: collection.id });
            const r = await api.get(`/learning/assessments?${params.toString()}`);
            setList(r.data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not load assessments"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [collection?.id]); // eslint-disable-line

    const filtered = useMemo(() => {
        if (scopeFilter === "all") return list;
        return list.filter((a) => a.scope_type === scopeFilter);
    }, [list, scopeFilter]);

    const counts = useMemo(() => ({
        all: list.length,
        collection: list.filter((a) => a.scope_type === "collection").length,
        folder:     list.filter((a) => a.scope_type === "folder").length,
        resource:   list.filter((a) => a.scope_type === "resource").length,
    }), [list]);

    const createNew = async () => {
        try {
            const r = await api.post("/learning/assessments", {
                title: "New assessment",
                scope_type: "collection",
                scope_id: collection.id,
                pass_threshold: 70,
                max_attempts: 1,
            });
            await load();
            setBuilder(r.data.id);
        } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    };

    const chip = (key, label) => (
        <button
            key={key}
            type="button"
            className={`asm-chip ${scopeFilter === key ? "active" : ""}`}
            onClick={() => setScopeFilter(key)}
            data-testid={`asm-chip-${key}`}
        >
            {label}
            <span className="asm-chip-count">{counts[key] ?? 0}</span>
        </button>
    );

    return (
        <div className="asm-panel" data-testid="asm-panel">
            <div className="asm-panel-hero">
                <div className="asm-panel-head">
                    <div>
                        <div className="asm-eyebrow"><Sparkles size={11}/> ODYSSEY ASSESSMENTS</div>
                        <h4>Prove it — one quiz at a time.</h4>
                        <div className="asm-sub">Attach quizzes, scenario tests &amp; questionnaires to the whole collection, a folder, or a single resource.</div>
                    </div>
                    <div className="asm-panel-head-cta">
                        <button type="button" className="asm-btn asm-btn-primary big" onClick={createNew} data-testid="asm-new-btn">
                            <Plus size={14} /><span>New<span className="asm-btn-label-long">&nbsp;assessment</span></span>
                        </button>
                    </div>
                </div>
                <div className="asm-chip-row">
                    {chip("all", "All")}
                    {chip("collection", "Collection")}
                    {chip("folder", "Folders")}
                    {chip("resource", "Resources")}
                </div>
            </div>

            {loading && <div className="asm-empty">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="asm-empty">
                    <Sparkles size={16} />
                    <div>
                        <div className="asm-empty-title">
                            {scopeFilter === "all" ? "No assessments yet" : `No ${scopeFilter}-scoped assessments yet`}
                        </div>
                        <div className="asm-empty-sub">Create one — attach it to the whole collection, a folder, or a single resource.</div>
                    </div>
                </div>
            )}
            <div className="asm-list">
                {filtered.map((a) => (
                    <AssessmentCard
                        key={a.id}
                        a={a}
                        userId={userId}
                        onEdit={() => setBuilder(a.id)}
                        onTake={() => setRunner(a.id)}
                        onViewResults={() => setResults({ attemptId: a.my_attempt?.id })}
                        onDeleted={load}
                    />
                ))}
            </div>
            {builder && (
                <BuilderDrawer
                    aid={builder}
                    collection={collection}
                    allFolders={folders}
                    allResources={resources}
                    onClose={() => { setBuilder(null); load(); }}
                />
            )}
            {runner && (
                <RunnerDialog
                    aid={runner}
                    onClose={() => { setRunner(null); load(); }}
                    onSubmitted={(attempt) => { setRunner(null); setResults({ attemptId: attempt.id, result: attempt }); load(); }}
                />
            )}
            {results && (
                <ResultDialog
                    attemptId={results.attemptId}
                    preloaded={results.result}
                    onClose={() => setResults(null)}
                    onRetry={() => { const aid = results.result?.assessment_id || results.aid; setResults(null); if (aid) setRunner(aid); }}
                />
            )}
        </div>
    );
};

/* ── Dark cockpit card for one assessment ─────────────────────────────── */
const AssessmentCard = ({ a, userId, onEdit, onTake, onViewResults, onDeleted }) => {
    const my = a.my_attempt;
    const isAuthor = a.created_by === userId;
    const canTake = a.is_published && (!a.max_attempts || !my || my.status === "in_progress" || a.max_attempts === 0);
    const del = async () => {
        if (!confirm(`Delete "${a.title}"? This can't be undone.`)) return;
        try { await api.delete(`/learning/assessments/${a.id}`); toast.success("Deleted"); onDeleted(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    const scope = SCOPE_META[a.scope_type] || SCOPE_META.collection;
    const ScopeIcon = scope.icon;
    const pct = my?.status === "graded" ? Math.round(my.percentage ?? ((my.score / Math.max(1, my.max_score)) * 100)) : null;
    const ringTone = pct == null ? "idle" : my.pass ? "pass" : "fail";
    return (
        <div className={`asm-card tone-${ringTone}`} data-testid={`asm-row-${a.id}`}>
            <div className="asm-card-mesh" aria-hidden />
            <div className="asm-card-inner">
                <div className="asm-card-main">
                    <div className="asm-card-scope" style={{ "--scope-color": scope.color }}>
                        <ScopeIcon size={11}/> {scope.label}
                        {a.scope_label && <span className="asm-card-scope-name">· {a.scope_label}</span>}
                    </div>
                    <div className="asm-card-title">{a.title}</div>
                    {a.description && <div className="asm-card-desc">{a.description}</div>}
                    <div className="asm-card-meta">
                        <span>{a.question_count || 0} Q</span>
                        <span>{a.time_limit_minutes ? `${a.time_limit_minutes} min` : "untimed"}</span>
                        <span>pass {a.pass_threshold}%</span>
                        <span>{a.max_attempts === 0 ? "unlimited" : `${a.max_attempts} attempt${a.max_attempts === 1 ? "" : "s"}`}</span>
                        {a.cert_on_pass && <span className="asm-card-badge">cert</span>}
                        {a.awards_xp && <span className="asm-card-badge">+XP</span>}
                        {!a.is_published && <span className="asm-card-badge warn">DRAFT</span>}
                    </div>
                </div>
                <div className="asm-card-right">
                    <ScoreChip value={pct} tone={ringTone} status={my?.status} />
                    <div className="asm-card-actions">
                        {my?.status === "graded" && (
                            <button type="button" className="asm-btn asm-btn-ghost" onClick={onViewResults} data-testid={`asm-view-${a.id}`}>
                                <Award size={13} /> Result
                            </button>
                        )}
                        {canTake && my?.status !== "graded" && (
                            <button type="button" className="asm-btn asm-btn-primary" onClick={onTake} data-testid={`asm-take-${a.id}`}>
                                <Play size={13} /> {my?.status === "in_progress" ? "Resume" : "Take"}
                            </button>
                        )}
                        {isAuthor && (
                            <>
                                <button type="button" className="asm-btn asm-btn-ghost" onClick={onEdit} data-testid={`asm-edit-${a.id}`}>
                                    <Pencil size={12} /> Edit
                                </button>
                                <button type="button" className="asm-icon-btn danger" title="Delete" onClick={del} data-testid={`asm-del-${a.id}`}><Trash2 size={13} /></button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Animated score ring (SVG) ─────────────────────────────────────────── */
const ScoreRing = ({ value, tone, status }) => {
    // Idle (never attempted) → dashed grey arc. In-progress → amber dots.
    // Graded → animated solid arc, teal/red based on tone.
    const r = 26, c = 2 * Math.PI * r;
    const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
    const dash = (pct / 100) * c;
    return (
        <div className={`asm-ring-wrap tone-${tone}`}>
            <svg viewBox="0 0 64 64" width="64" height="64" className="asm-ring-svg">
                <defs>
                    <linearGradient id={`asm-grad-${tone}`} x1="0" y1="0" x2="1" y2="1">
                        {tone === "pass" && (<><stop offset="0" stopColor="#2EB4AC"/><stop offset="1" stopColor="#56E8DF"/></>)}
                        {tone === "fail" && (<><stop offset="0" stopColor="#E05A3F"/><stop offset="1" stopColor="#F89078"/></>)}
                        {tone === "idle" && (<><stop offset="0" stopColor="#3C4A58"/><stop offset="1" stopColor="#5D6D7D"/></>)}
                    </linearGradient>
                </defs>
                <circle cx="32" cy="32" r={r} className="asm-ring-track" />
                {value != null && (
                    <circle
                        cx="32" cy="32" r={r}
                        stroke={`url(#asm-grad-${tone})`}
                        strokeWidth="5"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${dash} ${c - dash}`}
                        strokeDashoffset={c / 4}
                        transform="rotate(-90 32 32)"
                        className="asm-ring-arc"
                    />
                )}
            </svg>
            <div className="asm-ring-label">
                {value == null ? (
                    status === "in_progress"
                        ? <><span className="asm-ring-dots" aria-hidden/><em>in prog</em></>
                        : <em>new</em>
                ) : (
                    <><strong>{value}</strong><em>%</em></>
                )}
            </div>
        </div>
    );
};

/* ── Compact numeric score chip (used on assessment list cards) ────── */
const ScoreChip = ({ value, tone, status }) => {
    if (value != null) {
        return (
            <div className={`asm-score-chip tone-${tone}`} data-testid="asm-score-chip">
                {tone === "pass" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                <strong>{value}%</strong>
            </div>
        );
    }
    if (status === "in_progress") {
        return (
            <div className="asm-score-chip tone-progress" data-testid="asm-score-chip">
                <span className="asm-ring-dots" aria-hidden/><strong>In progress</strong>
            </div>
        );
    }
    return (
        <div className="asm-score-chip tone-idle" data-testid="asm-score-chip">
            <Sparkles size={12}/><strong>New</strong>
        </div>
    );
};


/* ── Builder (right-side slide-in drawer) ─────────────────────────────── */
const BuilderDrawer = ({ aid, collection, allFolders = [], allResources = [], onClose }) => {
    const [doc, setDoc] = useState(null);
    const [adding, setAdding] = useState(false);
    const [editingQ, setEditingQ] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [showAttempts, setShowAttempts] = useState(false);
    const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | err
    const saveTimer = useRef(null);

    const load = async () => {
        try { const r = await api.get(`/learning/assessments/${aid}?mode=author`); setDoc(r.data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    };
    useEffect(() => { load(); }, [aid]); // eslint-disable-line
    useEffect(() => {
        api.get(`/learning/assessments/${aid}/attempts`).then((r) => setAttempts(r.data || [])).catch(() => {});
    }, [aid]);

    const patch = async (updates) => {
        setDoc((d) => ({ ...d, ...updates }));
        setSaveStatus("saving");
        try {
            await api.patch(`/learning/assessments/${aid}`, updates);
            if ("scope_type" in updates || "scope_id" in updates) load();
            setSaveStatus("saved");
            if (saveTimer.current) clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => setSaveStatus("idle"), 1800);
        } catch (e) {
            setSaveStatus("err");
            toast.error(e?.response?.data?.detail || "Save failed");
            load();
        }
    };
    const deleteQ = async (qid) => {
        if (!confirm("Delete this question?")) return;
        try { await api.delete(`/learning/assessments/${aid}/questions/${qid}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    const moveQ = async (qid, dir) => {
        const qs = doc.questions || [];
        const idx = qs.findIndex((q) => q.id === qid);
        const to = idx + dir;
        if (idx === -1 || to < 0 || to >= qs.length) return;
        const next = [...qs]; const [moved] = next.splice(idx, 1); next.splice(to, 0, moved);
        setDoc((d) => ({ ...d, questions: next }));
        try { await api.put(`/learning/assessments/${aid}/questions/reorder`, { question_ids: next.map((q) => q.id) }); }
        catch (e) { toast.error(e?.response?.data?.detail || "Reorder failed"); load(); }
    };

    if (!doc) return (
        <div className="asm-b2-overlay" onClick={onClose}>
            <div className="asm-b2-drawer loading" onClick={(e) => e.stopPropagation()}>Loading…</div>
        </div>
    );
    const scopeType = doc.scope_type || "collection";
    const scopeId = doc.scope_id || collection.id;
    const questionCount = (doc.questions || []).length;
    return (
        <div className="asm-b2-overlay" onClick={onClose}>
            <div className="asm-b2-drawer" onClick={(e) => e.stopPropagation()} data-testid="asm-builder">
                <button type="button" className="asm-b2-close" onClick={onClose} aria-label="Close" data-testid="asm-builder-close"><X size={18} /></button>

                <div className="asm-b2-head">
                    <div className="asm-b2-eyebrow">
                        AUTHORING
                        {saveStatus !== "idle" && (
                            <span className={`asm-autosave ${saveStatus}`} data-testid="asm-autosave">
                                <span className="asm-autosave-dot"/>
                                {saveStatus === "saving" ? "Saving" : saveStatus === "saved" ? "Saved" : "Retry"}
                            </span>
                        )}
                    </div>
                    <input
                        className="asm-b2-title"
                        value={doc.title}
                        onChange={(e) => setDoc({ ...doc, title: e.target.value })}
                        onBlur={() => patch({ title: doc.title })}
                        placeholder="Assessment title"
                        data-testid="asm-title-input"
                    />
                </div>

                <div className="asm-b2-body">
                    {/* ── Attach to ─────────────────────────────────────────── */}
                    <section className="asm-b2-sec">
                        <div className="asm-b2-section-label">Attach to</div>
                        <div className="asm-b2-scope-grid" role="tablist">
                            {["collection", "folder", "resource"].map((k) => {
                                const Meta = SCOPE_META[k];
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        className={`asm-b2-scope-btn ${scopeType === k ? "active" : ""}`}
                                        onClick={() => {
                                            if (k === "collection") patch({ scope_type: "collection", scope_id: collection.id });
                                            else if (k === "folder") patch({ scope_type: "folder", scope_id: allFolders[0]?.id || "" });
                                            else patch({ scope_type: "resource", scope_id: allResources[0]?.id || "" });
                                        }}
                                        data-testid={`asm-scope-tab-${k}`}
                                    >
                                        {Meta.label}
                                    </button>
                                );
                            })}
                        </div>
                        {scopeType === "collection" && (
                            <div className="asm-b2-scope-caption">Attached to <strong>{collection.name}</strong>. Visible on the Assessments tab for everyone with access.</div>
                        )}
                        {scopeType === "folder" && (
                            <select
                                className="asm-b2-input"
                                value={scopeId}
                                onChange={(e) => patch({ scope_type: "folder", scope_id: e.target.value })}
                                data-testid="asm-scope-folder-select"
                            >
                                {allFolders.length === 0 && <option value="">(no folders in this collection)</option>}
                                {allFolders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                        )}
                        {scopeType === "resource" && (
                            <select
                                className="asm-b2-input"
                                value={scopeId}
                                onChange={(e) => patch({ scope_type: "resource", scope_id: e.target.value })}
                                data-testid="asm-scope-resource-select"
                            >
                                {allResources.length === 0 && <option value="">(no resources in this collection)</option>}
                                {allResources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        )}
                    </section>

                    {/* ── Overview ───────────────────────────────────────────── */}
                    <section className="asm-b2-sec">
                        <div className="asm-b2-section-label">Overview</div>
                        <input
                            className="asm-b2-input"
                            value={doc.description || ""}
                            onChange={(e) => setDoc({ ...doc, description: e.target.value })}
                            onBlur={() => patch({ description: doc.description })}
                            placeholder="One-line description (optional)"
                        />
                    </section>

                    {/* ── Rules table ────────────────────────────────────────── */}
                    <section className="asm-b2-sec">
                        <div className="asm-b2-section-label">Rules</div>
                        <div className="asm-b2-table">
                            <div className="asm-b2-table-head cols-rule">
                                <div>Rule</div>
                                <div>Value</div>
                            </div>
                            <div className="asm-b2-table-row cols-rule">
                                <div className="asm-b2-rule-name">Pass threshold %</div>
                                <div className="asm-b2-rule-value">
                                    <input
                                        type="number" min={0} max={100}
                                        className="asm-b2-input-cell"
                                        value={doc.pass_threshold ?? 70}
                                        onChange={(e) => setDoc({ ...doc, pass_threshold: Number(e.target.value) })}
                                        onBlur={() => patch({ pass_threshold: doc.pass_threshold })}
                                        data-testid="asm-pass-input"
                                    />
                                </div>
                            </div>
                            <div className="asm-b2-table-row cols-rule">
                                <div className="asm-b2-rule-name">Time limit · min</div>
                                <div className="asm-b2-rule-value">
                                    <input
                                        type="number" min={0}
                                        className="asm-b2-input-cell"
                                        placeholder="None"
                                        value={doc.time_limit_minutes ?? ""}
                                        onChange={(e) => setDoc({ ...doc, time_limit_minutes: e.target.value === "" ? null : Number(e.target.value) })}
                                        onBlur={() => patch({ time_limit_minutes: doc.time_limit_minutes })}
                                    />
                                </div>
                            </div>
                            <div className="asm-b2-table-row cols-rule">
                                <div className="asm-b2-rule-name">Max attempts (0 = ∞)</div>
                                <div className="asm-b2-rule-value">
                                    <input
                                        type="number" min={0}
                                        className="asm-b2-input-cell"
                                        value={doc.max_attempts ?? 1}
                                        onChange={(e) => setDoc({ ...doc, max_attempts: Number(e.target.value) })}
                                        onBlur={() => patch({ max_attempts: doc.max_attempts })}
                                    />
                                </div>
                            </div>
                            <div className="asm-b2-table-row cols-rule">
                                <div className="asm-b2-rule-name">Certificate on pass</div>
                                <div className="asm-b2-rule-value">
                                    <B2Switch checked={!!doc.cert_on_pass} onChange={(v) => patch({ cert_on_pass: v })} />
                                </div>
                            </div>
                            <div className="asm-b2-table-row cols-rule">
                                <div className="asm-b2-rule-name">Award XP on pass</div>
                                <div className="asm-b2-rule-value">
                                    <B2Switch checked={!!doc.awards_xp} onChange={(v) => patch({ awards_xp: v })} />
                                </div>
                            </div>
                            <div className="asm-b2-table-row cols-rule last">
                                <div className="asm-b2-rule-name">Shuffle questions</div>
                                <div className="asm-b2-rule-value">
                                    <B2Switch checked={!!doc.shuffle_questions} onChange={(v) => patch({ shuffle_questions: v })} />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── Questions ─────────────────────────────────────────── */}
                    <section className="asm-b2-sec">
                        <div className="asm-b2-section-head">
                            <div className="asm-b2-section-label">Questions · {questionCount}</div>
                            <button type="button" className="asm-b2-btn-outline" onClick={() => setAdding(true)} data-testid="asm-add-q">
                                <Plus size={13} /> Add question
                            </button>
                        </div>
                        <div className="asm-b2-table">
                            <div className="asm-b2-table-head cols-q">
                                <div>#</div>
                                <div>Question</div>
                                <div style={{ textAlign: "right" }}>Actions</div>
                            </div>
                            {questionCount === 0 ? (
                                <div className="asm-b2-table-empty">No questions yet. Add your first one.</div>
                            ) : (
                                (doc.questions || []).map((q, i) => (
                                    <QuestionRow
                                        key={q.id}
                                        q={q}
                                        idx={i}
                                        total={questionCount}
                                        onEdit={() => setEditingQ(q)}
                                        onDelete={() => deleteQ(q.id)}
                                        onMoveUp={() => moveQ(q.id, -1)}
                                        onMoveDown={() => moveQ(q.id, +1)}
                                    />
                                ))
                            )}
                        </div>
                    </section>

                    {/* ── Trainee attempts ──────────────────────────────────── */}
                    <section className="asm-b2-sec">
                        <div className="asm-b2-section-head">
                            <div className="asm-b2-section-label">Trainee attempts · {attempts.length}</div>
                            {!showAttempts && attempts.length === 0 && (
                                <button type="button" className="asm-b2-btn-link" onClick={() => setShowAttempts(true)}>Load</button>
                            )}
                        </div>
                        <div className="asm-b2-table">
                            <div className="asm-b2-table-head cols-attempts">
                                <div>Trainee</div>
                                <div>Attempt</div>
                                <div>Score</div>
                                <div>Date</div>
                            </div>
                            {attempts.length === 0 ? (
                                <div className="asm-b2-table-empty">No attempts yet</div>
                            ) : (
                                attempts.map((at) => {
                                    const pct = Math.round(at.percentage ?? ((at.score / Math.max(1, at.max_score)) * 100));
                                    return (
                                        <div key={at.id} className="asm-b2-table-row cols-attempts" data-testid={`asm-attempt-row-${at.id}`}>
                                            <div className="asm-b2-cell-strong">{at.user_name || at.user_id}</div>
                                            <div className="asm-b2-cell-muted">#{at.attempt_number ?? "—"}</div>
                                            <div>
                                                <span className={`asm-b2-score-pill ${at.pass ? "pass" : "fail"}`}>
                                                    {at.pass ? <CheckCircle2 size={10}/> : <XCircle size={10}/>} {pct}%
                                                </span>
                                            </div>
                                            <div className="asm-b2-cell-muted">{at.submitted_at?.slice(0, 10) || "—"}</div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </section>
                </div>

                <div className="asm-b2-foot">
                    <label className="asm-b2-check">
                        <input
                            type="checkbox"
                            checked={!!doc.is_published}
                            onChange={(e) => patch({ is_published: e.target.checked })}
                            data-testid="asm-publish-toggle"
                        />
                        <span><strong>Publish:</strong> trainees can take this</span>
                    </label>
                    <button type="button" className="asm-b2-btn-primary" onClick={onClose} data-testid="asm-builder-done">Done</button>
                </div>
            </div>
            {(adding || editingQ) && (
                <QuestionDialog
                    aid={aid}
                    initial={editingQ || emptyQuestion()}
                    isEdit={!!editingQ}
                    onClose={() => { setAdding(false); setEditingQ(null); }}
                    onSaved={() => { setAdding(false); setEditingQ(null); load(); }}
                />
            )}
        </div>
    );
};

/* ── Scope picker ──────────────────────────────────────────────────────── */
/* ── Dark-teal toggle switch (builder v2) ─────────────────────────────── */
const B2Switch = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`asm-b2-switch ${checked ? "on" : "off"}`}
        onClick={() => onChange(!checked)}
        data-testid={`asm-b2-switch-${checked ? "on" : "off"}`}
    >
        <span className="asm-b2-switch-thumb"/>
    </button>
);

/* ── Question table row (builder v2) ──────────────────────────────────── */
const QuestionRow = ({ q, idx, total, onEdit, onDelete, onMoveUp, onMoveDown }) => {
    const typeLabel = (QTYPES.find((t) => t.key === q.type) || {}).label || q.type;
    return (
        <div className="asm-b2-table-row cols-q" data-testid={`asm-qcard-${q.id}`}>
            <div className="asm-b2-cell-rank">{idx + 1}</div>
            <div className="asm-b2-cell-q">
                <div className="asm-b2-cell-q-prompt">{q.prompt || <em>(no prompt)</em>}</div>
                <div className="asm-b2-cell-q-meta">{typeLabel} · {q.points} pt{q.points === 1 ? "" : "s"}</div>
            </div>
            <div className="asm-b2-cell-actions">
                <button type="button" className="asm-b2-icon-btn" title="Move up" disabled={idx === 0} onClick={onMoveUp}><ChevronUp size={14}/></button>
                <button type="button" className="asm-b2-icon-btn" title="Move down" disabled={idx === total - 1} onClick={onMoveDown}><ChevronDown size={14}/></button>
                <button type="button" className="asm-b2-icon-btn" title="Edit" onClick={onEdit}><Edit3 size={14}/></button>
                <button type="button" className="asm-b2-icon-btn danger" title="Delete" onClick={onDelete}><Trash2 size={14}/></button>
            </div>
        </div>
    );
};

/* ── Legacy ScopePicker (kept for backward compat if invoked elsewhere) ── */
const ScopePicker = ({ scopeType, scopeId, collection, allFolders, allResources, onChange }) => {
    const [t, setT] = useState(scopeType || "collection");
    const [id, setId] = useState(scopeId || collection.id);
    useEffect(() => { setT(scopeType || "collection"); setId(scopeId || collection.id); }, [scopeType, scopeId, collection.id]);
    const commit = (nt, nid) => { setT(nt); setId(nid); onChange(nt, nid); };
    return (
        <div className="asm-scope">
            <div className="asm-scope-tabs">
                {["collection", "folder", "resource"].map((k) => {
                    const Meta = SCOPE_META[k];
                    const Icon = Meta.icon;
                    return (
                        <button
                            key={k}
                            type="button"
                            className={`asm-scope-tab ${t === k ? "active" : ""}`}
                            style={{ "--tab-color": Meta.color }}
                            onClick={() => {
                                if (k === "collection") commit("collection", collection.id);
                                else if (k === "folder") commit("folder", allFolders[0]?.id || "");
                                else commit("resource", allResources[0]?.id || "");
                            }}
                            data-testid={`asm-scope-tab-${k}`}
                        ><Icon size={12}/> {Meta.label}</button>
                    );
                })}
            </div>
            {t === "collection" && (
                <div className="asm-scope-info">Attached to <strong>{collection.name}</strong>. Visible on the Assessments tab for everyone with access.</div>
            )}
            {t === "folder" && (
                <select className="asm-input" value={id} onChange={(e) => commit("folder", e.target.value)} data-testid="asm-scope-folder-select">
                    {allFolders.length === 0 && <option value="">(no folders in this collection)</option>}
                    {allFolders.map((f) => <option key={f.id} value={f.id}>📁 {f.name}</option>)}
                </select>
            )}
            {t === "resource" && (
                <select className="asm-input" value={id} onChange={(e) => commit("resource", e.target.value)} data-testid="asm-scope-resource-select">
                    {allResources.length === 0 && <option value="">(no resources in this collection)</option>}
                    {allResources.map((r) => <option key={r.id} value={r.id}>📄 {r.name}</option>)}
                </select>
            )}
        </div>
    );
};

/* ── Question card (builder) ──────────────────────────────────────────── */
const QuestionCard = ({ q, idx, total, onEdit, onDelete, onMoveUp, onMoveDown }) => {
    const typeLabel = (QTYPES.find((t) => t.key === q.type) || {}).label || q.type;
    return (
        <div className="asm-qcard" data-testid={`asm-qcard-${q.id}`}>
            <div className="asm-qcard-rank">{idx + 1}</div>
            <div className="asm-qcard-body">
                <div className="asm-qcard-type">{typeLabel} · {q.points} pt{q.points === 1 ? "" : "s"}</div>
                <div className="asm-qcard-prompt">{q.prompt || <em>(no prompt)</em>}</div>
                {q.options && (
                    <ul className="asm-qcard-opts">
                        {q.options.map((o, i) => {
                            const isCorrect = q.type === "mcq_single" ? q.correct_answer === i :
                                              q.type === "mcq_multi" ? (q.correct_answer || []).includes(i) : false;
                            return <li key={i} className={isCorrect ? "correct" : ""}>{isCorrect && "✓ "}{o}</li>;
                        })}
                    </ul>
                )}
                {q.type === "true_false" && <div className="asm-qcard-ans">Correct: <strong>{String(q.correct_answer)}</strong></div>}
                {q.type === "short" && <div className="asm-qcard-ans">Expected: <strong>{q.correct_answer}</strong></div>}
                {q.type === "long" && q.rubric && <div className="asm-qcard-ans">Rubric: {q.rubric}</div>}
            </div>
            <div className="asm-qcard-actions">
                <button type="button" className="asm-icon-btn" title="Move up" disabled={idx === 0} onClick={onMoveUp}><ChevronUp size={13} /></button>
                <button type="button" className="asm-icon-btn" title="Move down" disabled={idx === total - 1} onClick={onMoveDown}><ChevronDown size={13} /></button>
                <button type="button" className="asm-icon-btn" title="Edit" onClick={onEdit}><Edit3 size={13} /></button>
                <button type="button" className="asm-icon-btn danger" title="Delete" onClick={onDelete}><Trash2 size={13} /></button>
            </div>
        </div>
    );
};

/* ── Question add/edit dialog ─────────────────────────────────────────── */
const QuestionDialog = ({ aid, initial, isEdit, onClose, onSaved }) => {
    const [q, setQ] = useState(initial);
    const setField = (k, v) => setQ((prev) => ({ ...prev, [k]: v }));
    const setOption = (i, v) => setQ((prev) => ({ ...prev, options: prev.options.map((o, j) => (j === i ? v : o)) }));
    const addOption = () => setQ((prev) => ({ ...prev, options: [...(prev.options || []), ""] }));
    const removeOption = (i) => setQ((prev) => {
        const nextOpts = prev.options.filter((_, j) => j !== i);
        let nextAns = prev.correct_answer;
        if (prev.type === "mcq_single") { if (nextAns === i) nextAns = 0; else if (nextAns > i) nextAns -= 1; }
        else if (prev.type === "mcq_multi") { nextAns = (prev.correct_answer || []).filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)); }
        return { ...prev, options: nextOpts, correct_answer: nextAns };
    });

    const save = async () => {
        if (!q.prompt.trim()) { toast.error("Prompt is required"); return; }
        try {
            if (isEdit) await api.put(`/learning/assessments/${aid}/questions/${q.id}`, q);
            else await api.post(`/learning/assessments/${aid}/questions`, q);
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    return (
        <div className="asm-modal-overlay" onClick={onClose}>
            <div className="asm-modal" onClick={(e) => e.stopPropagation()} data-testid="asm-qdialog">
                <div className="asm-drawer-head">
                    <div className="asm-drawer-head-left">
                        <div className="asm-eyebrow">{isEdit ? "EDIT QUESTION" : "NEW QUESTION"}</div>
                        <div className="asm-modal-title">{(QTYPES.find((x) => x.key === q.type) || {}).label}</div>
                    </div>
                    <button type="button" className="asm-icon-btn" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="asm-drawer-body">
                    <label className="asm-field">
                        <span>Type</span>
                        <select className="asm-input" value={q.type} onChange={(e) => setQ(emptyQuestion(e.target.value))} data-testid="asm-qtype">
                            {QTYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </label>
                    <label className="asm-field">
                        <span>Prompt</span>
                        <textarea className="asm-input" rows={2} value={q.prompt} onChange={(e) => setField("prompt", e.target.value)} data-testid="asm-qprompt" placeholder="What are you asking?" />
                    </label>
                    <label className="asm-field inline">
                        <span>Points</span>
                        <input className="asm-input" type="number" min={1} value={q.points} onChange={(e) => setField("points", Math.max(1, Number(e.target.value)))} />
                    </label>

                    {(q.type === "mcq_single" || q.type === "mcq_multi") && (
                        <div className="asm-field">
                            <span>Options {q.type === "mcq_multi" ? "(tick all correct)" : "(tick the correct one)"}</span>
                            {(q.options || []).map((o, i) => (
                                <div key={i} className="asm-opt-row">
                                    <input
                                        type={q.type === "mcq_single" ? "radio" : "checkbox"}
                                        name="mcq-correct"
                                        checked={q.type === "mcq_single" ? q.correct_answer === i : (q.correct_answer || []).includes(i)}
                                        onChange={() => {
                                            if (q.type === "mcq_single") setField("correct_answer", i);
                                            else {
                                                const next = new Set(q.correct_answer || []);
                                                if (next.has(i)) next.delete(i); else next.add(i);
                                                setField("correct_answer", Array.from(next).sort());
                                            }
                                        }}
                                    />
                                    <input className="asm-input opt" value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} />
                                    <button type="button" className="asm-icon-btn" onClick={() => removeOption(i)}><Trash2 size={12} /></button>
                                </div>
                            ))}
                            <button type="button" className="asm-btn asm-btn-ghost" onClick={addOption}><Plus size={12}/> Add option</button>
                        </div>
                    )}
                    {q.type === "true_false" && (
                        <label className="asm-field">
                            <span>Correct answer</span>
                            <select className="asm-input" value={q.correct_answer ? "true" : "false"} onChange={(e) => setField("correct_answer", e.target.value === "true")}>
                                <option value="true">True</option><option value="false">False</option>
                            </select>
                        </label>
                    )}
                    {q.type === "short" && (
                        <label className="asm-field">
                            <span>Expected answer (fuzzy match)</span>
                            <input className="asm-input" value={q.correct_answer || ""} onChange={(e) => setField("correct_answer", e.target.value)} placeholder="e.g. impressions" />
                        </label>
                    )}
                    {q.type === "long" && (
                        <label className="asm-field">
                            <span>Rubric (Claude grades against this)</span>
                            <textarea className="asm-input" rows={3} value={q.rubric || ""} onChange={(e) => setField("rubric", e.target.value)} placeholder="What a full-credit answer must include…" />
                        </label>
                    )}
                    {q.type === "likert" && (
                        <div className="asm-field">
                            <span>Scale (min → max + labels)</span>
                            <div className="asm-likert-cfg">
                                <input className="asm-input" type="number" min={1} value={q.likert_scale?.min ?? 1} onChange={(e) => setField("likert_scale", { ...q.likert_scale, min: Number(e.target.value) })} />
                                <input className="asm-input" type="number" min={2} value={q.likert_scale?.max ?? 5} onChange={(e) => setField("likert_scale", { ...q.likert_scale, max: Number(e.target.value) })} />
                                <input className="asm-input" value={q.likert_scale?.left_label || ""} placeholder="Left label" onChange={(e) => setField("likert_scale", { ...q.likert_scale, left_label: e.target.value })} />
                                <input className="asm-input" value={q.likert_scale?.right_label || ""} placeholder="Right label" onChange={(e) => setField("likert_scale", { ...q.likert_scale, right_label: e.target.value })} />
                            </div>
                        </div>
                    )}
                    {q.type === "scenario" && (
                        <ScenarioEditor branches={q.scenario_branches || []} onChange={(b) => setField("scenario_branches", b)} />
                    )}
                    {q.type === "file" && (
                        <div className="asm-note">Trainees will see a file-upload field; the file is marked submitted — manual review in a later release.</div>
                    )}
                    <label className="asm-field">
                        <span>Explanation shown after submit (optional)</span>
                        <input className="asm-input" value={q.explanation || ""} onChange={(e) => setField("explanation", e.target.value)} placeholder="Why this is the answer…" />
                    </label>
                </div>
                <div className="asm-drawer-foot">
                    <button type="button" className="asm-btn asm-btn-ghost" onClick={onClose}>Cancel</button>
                    <button type="button" className="asm-btn asm-btn-primary" onClick={save} data-testid="asm-qdialog-save">
                        {isEdit ? "Save changes" : "Add question"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ScenarioEditor = ({ branches, onChange }) => {
    const addBranch = () => onChange([...branches, { id: `b_${Date.now()}`, prompt: "", choices: [{ id: `c1_${Date.now()}`, label: "", is_correct: true }, { id: `c2_${Date.now()}`, label: "", is_correct: false }] }]);
    const update = (bi, patch) => onChange(branches.map((b, i) => (i === bi ? { ...b, ...patch } : b)));
    const removeBranch = (bi) => onChange(branches.filter((_, i) => i !== bi));
    const addChoice = (bi) => update(bi, { choices: [...(branches[bi].choices || []), { id: `c_${Date.now()}`, label: "", is_correct: false }] });
    const updChoice = (bi, ci, patch) => update(bi, { choices: branches[bi].choices.map((c, j) => (j === ci ? { ...c, ...patch } : c)) });
    const removeChoice = (bi, ci) => update(bi, { choices: branches[bi].choices.filter((_, j) => j !== ci) });
    return (
        <div className="asm-field">
            <span>Scenario branches</span>
            {branches.map((b, bi) => (
                <div key={b.id} className="asm-branch">
                    <div className="asm-branch-head">
                        <input className="asm-input" value={b.prompt} placeholder={`Step ${bi + 1} — what happens next?`} onChange={(e) => update(bi, { prompt: e.target.value })} />
                        <button type="button" className="asm-icon-btn" onClick={() => removeBranch(bi)}><Trash2 size={12}/></button>
                    </div>
                    {b.choices.map((c, ci) => (
                        <div key={c.id} className="asm-opt-row">
                            <input type="radio" checked={!!c.is_correct}
                                onChange={() => update(bi, { choices: b.choices.map((x, j) => ({ ...x, is_correct: j === ci })) })} />
                            <input className="asm-input opt" value={c.label} placeholder={`Choice ${ci + 1}`} onChange={(e) => updChoice(bi, ci, { label: e.target.value })} />
                            <button type="button" className="asm-icon-btn" onClick={() => removeChoice(bi, ci)}><Trash2 size={12}/></button>
                        </div>
                    ))}
                    <button type="button" className="asm-btn asm-btn-ghost" onClick={() => addChoice(bi)}><Plus size={11}/> Add choice</button>
                </div>
            ))}
            <button type="button" className="asm-btn asm-btn-ghost" onClick={addBranch}><Plus size={12}/> Add branch</button>
        </div>
    );
};

/* ── Trainee runner ────────────────────────────────────────────────────── */
export const RunnerDialog = ({ aid, onClose, onSubmitted }) => {
    const [doc, setDoc] = useState(null);
    const [attempt, setAttempt] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [idx, setIdx] = useState(0);
    const [remaining, setRemaining] = useState(null);

    const submit = async () => {
        if (submitting || !attempt) return;
        setSubmitting(true);
        try {
            const r = await api.post(`/learning/assessments/attempts/${attempt.id}/submit`);
            onSubmitted(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); setSubmitting(false); }
    };

    const start = async () => {
        try {
            const d = await api.get(`/learning/assessments/${aid}?mode=take`);
            setDoc(d.data);
            const a = await api.post(`/learning/assessments/${aid}/attempts`);
            setAttempt(a.data);
            setAnswers(a.data.answers || {});
            if (a.data.deadline) {
                const left = Math.max(0, Math.floor((new Date(a.data.deadline).getTime() - Date.now()) / 1000));
                setRemaining(left);
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start attempt"); onClose(); }
    };
    useEffect(() => { start(); }, [aid]); // eslint-disable-line
    useEffect(() => {
        if (remaining == null) return;
        if (remaining <= 0) { submit(); return; }
        const t = setTimeout(() => setRemaining((r) => (r == null ? r : r - 1)), 1000);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [remaining]);

    const setAns = async (qid, v) => {
        setAnswers((prev) => ({ ...prev, [qid]: v }));
        try { await api.put(`/learning/assessments/attempts/${attempt.id}/answer`, { question_id: qid, answer: v }); }
        catch (e) { /* silent — trainee can retry on submit */ }
    };

    // Keyboard shortcuts: ← / → to move, Ctrl+Enter to submit on the last question.
    useEffect(() => {
        if (!doc || !attempt) return;
        const onKey = (e) => {
            if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) {
                if (!(e.key === "Enter" && (e.ctrlKey || e.metaKey))) return;
            }
            if (e.key === "ArrowRight" && idx < (doc.questions?.length || 1) - 1) { setIdx(idx + 1); }
            if (e.key === "ArrowLeft" && idx > 0) { setIdx(idx - 1); }
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { submit(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line
    }, [idx, doc, attempt]);

    if (!doc || !attempt) return (
        <div className="asm-runner-overlay" onClick={onClose}><div className="asm-runner-shell"><div className="asm-empty">Loading…</div></div></div>
    );
    const q = doc.questions[idx];
    const mins = remaining != null ? Math.floor(remaining / 60) : null;
    const secs = remaining != null ? remaining % 60 : null;
    return (
        <div className="asm-runner-overlay">
            <div className="asm-runner-shell" data-testid="asm-runner">
                <div className="asm-runner-grid">
                    <div className="asm-runner-grid-lines" aria-hidden/>
                </div>
                <div className="asm-runner-head">
                    <div>
                        <div className="asm-eyebrow"><Play size={10}/> ATTEMPT IN PROGRESS</div>
                        <div className="asm-runner-title">{doc.title}</div>
                    </div>
                    <div className="asm-runner-controls">
                        {remaining != null && (
                            <span className={`asm-pill ${remaining <= 60 ? "fail" : "in-progress"}`}>
                                <Clock size={12}/> {mins}:{String(secs).padStart(2, "0")}
                            </span>
                        )}
                        <button type="button" className="asm-icon-btn" onClick={onClose} aria-label="Save & close"><X size={16} /></button>
                    </div>
                </div>
                <div className="asm-runner-progress">
                    <div className="asm-runner-progress-bar" style={{ width: `${((idx + 1) / Math.max(1, doc.questions.length)) * 100}%` }} />
                </div>
                {doc.questions && doc.questions.length > 1 && (
                    <div className="asm-navigator" data-testid="asm-navigator">
                        {doc.questions.map((qq, qi) => {
                            const ans = answers[qq.id];
                            const answered = ans !== undefined && ans !== null && ans !== "" && !(Array.isArray(ans) && ans.length === 0);
                            const cls = `asm-nav-pill ${answered ? "answered" : ""} ${qi === idx ? "current" : ""}`;
                            return (
                                <button
                                    key={qq.id}
                                    type="button"
                                    className={cls}
                                    onClick={() => setIdx(qi)}
                                    title={`Question ${qi + 1}${answered ? " · answered" : ""}`}
                                    data-testid={`asm-nav-${qi}`}
                                >
                                    {qi + 1}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="asm-runner-body">
                    {!q ? <div className="asm-empty-inline">This assessment has no questions yet.</div> : (
                        <>
                            <div className="asm-runner-step">Question {idx + 1} of {doc.questions.length} · {q.points} pt{q.points === 1 ? "" : "s"}</div>
                            <div className="asm-runner-prompt">{q.prompt}</div>
                            <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAns(q.id, v)} />
                        </>
                    )}
                </div>
                <div className="asm-runner-foot">
                    <button type="button" className="asm-btn asm-btn-ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>Back</button>
                    {idx < doc.questions.length - 1 ? (
                        <button type="button" className="asm-btn asm-btn-primary" onClick={() => setIdx(idx + 1)} data-testid="asm-runner-next">Next →</button>
                    ) : (
                        <button type="button" className="asm-btn asm-btn-primary" disabled={submitting} onClick={submit} data-testid="asm-runner-submit">
                            {submitting ? "Grading…" : "Submit"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const QuestionInput = ({ q, value, onChange }) => {
    if (q.type === "mcq_single") {
        return (
            <div className="asm-input-list">
                {(q.options || []).map((o, i) => (
                    <label key={i} className={`asm-opt-choice ${value === i ? "selected" : ""}`}>
                        <input type="radio" checked={value === i} onChange={() => onChange(i)} /><span>{o}</span>
                    </label>
                ))}
            </div>
        );
    }
    if (q.type === "mcq_multi") {
        const arr = Array.isArray(value) ? value : [];
        return (
            <div className="asm-input-list">
                {(q.options || []).map((o, i) => (
                    <label key={i} className={`asm-opt-choice ${arr.includes(i) ? "selected" : ""}`}>
                        <input type="checkbox" checked={arr.includes(i)} onChange={() => {
                            const next = new Set(arr); if (next.has(i)) next.delete(i); else next.add(i);
                            onChange(Array.from(next).sort());
                        }} /><span>{o}</span>
                    </label>
                ))}
            </div>
        );
    }
    if (q.type === "true_false") {
        return (
            <div className="asm-input-list">
                <label className={`asm-opt-choice ${value === true ? "selected" : ""}`}><input type="radio" checked={value === true} onChange={() => onChange(true)}/><span>True</span></label>
                <label className={`asm-opt-choice ${value === false ? "selected" : ""}`}><input type="radio" checked={value === false} onChange={() => onChange(false)}/><span>False</span></label>
            </div>
        );
    }
    if (q.type === "short") {
        return <input className="asm-input-text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type your answer…" data-testid="asm-input-short"/>;
    }
    if (q.type === "long") {
        return <textarea className="asm-input-text" rows={6} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Write a detailed answer…" data-testid="asm-input-long"/>;
    }
    if (q.type === "likert") {
        const scale = q.likert_scale || { min: 1, max: 5 };
        const nums = []; for (let i = scale.min; i <= scale.max; i++) nums.push(i);
        return (
            <div>
                <div className="asm-likert-labels"><span>{scale.left_label}</span><span>{scale.right_label}</span></div>
                <div className="asm-likert">
                    {nums.map((n) => <button key={n} type="button" className={`asm-likert-btn ${value === n ? "selected" : ""}`} onClick={() => onChange(n)}>{n}</button>)}
                </div>
            </div>
        );
    }
    if (q.type === "scenario") {
        const picks = Array.isArray(value) ? value : [];
        return (
            <div>
                {(q.scenario_branches || []).map((b, bi) => (
                    <div key={b.id} className="asm-scenario-step">
                        <div className="asm-scenario-prompt"><strong>Step {bi + 1}:</strong> {b.prompt}</div>
                        {b.choices.map((c) => (
                            <label key={c.id} className={`asm-opt-choice ${picks[bi] === c.id ? "selected" : ""}`}>
                                <input type="radio" checked={picks[bi] === c.id} onChange={() => {
                                    const next = [...picks]; next[bi] = c.id; onChange(next);
                                }} /><span>{c.label}</span>
                            </label>
                        ))}
                    </div>
                ))}
            </div>
        );
    }
    if (q.type === "file") {
        return <input type="file" onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange({ name: f.name, size: f.size, type: f.type }); }} />;
    }
    return null;
};

/* ── Results dialog ────────────────────────────────────────────────────── */
export const ResultDialog = ({ attemptId, preloaded, onClose, onRetry }) => {
    const [data, setData] = useState(preloaded || null);
    const [reloaded, setReloaded] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const { user: me } = useAuth() || {};

    const fetchFull = React.useCallback(() => {
        if (!attemptId) return;
        api.get(`/learning/assessments/attempts/${attemptId}`)
            .then((r) => { setData(r.data); setReloaded(true); })
            .catch(() => toast.error("Could not load result"));
    }, [attemptId]);

    useEffect(() => {
        // If we have a preloaded submit response but no `questions`, re-fetch to get
        // correct-answer reveals. Otherwise, fetch cold.
        if (!data) { fetchFull(); return; }
        if (!reloaded && !data.questions) { fetchFull(); }
    }, [data, fetchFull, reloaded]);

    // Auto-open the feedback section when the trainee failed so they land directly on reviewable items.
    useEffect(() => {
        if (data && data.pass === false) setShowFeedback(true);
    }, [data]);

    if (!data) return (
        <div className="asm-r2-overlay" onClick={onClose}><div className="asm-r2-shell loading">Loading…</div></div>
    );
    const pct = Math.round(data.percentage ?? ((data.score / Math.max(1, data.max_score)) * 100));
    const threshold = data.threshold ?? 70;
    const feedback = data.feedback || {};
    const questions = data.questions || [];
    const qById = Object.fromEntries(questions.map((q) => [q.id, q]));
    const passed = !!data.pass;
    const certEligible = passed && (data.cert_on_pass ?? true);
    const canRetry = !passed && (data.max_attempts === 0 || (data.attempts_used ?? 1) < (data.max_attempts ?? 1));
    const displayName = data.user_name || me?.name || "Odyssey Trainee";
    const issuedAt = data.submitted_at ? new Date(data.submitted_at) : new Date();
    const shortDate = issuedAt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    const certId = `OD-${String(attemptId || data.id || "").slice(-6).toUpperCase() || "000000"}`;

    // Headline chosen by score band
    const headline = passed
        ? pct === 100 ? "Gold medal work!"
            : pct >= 90 ? "Brilliant!"
            : pct >= 80 ? "Nicely done!"
            : "You passed!"
        : pct >= threshold - 10 ? "So close."
            : "Not this time.";
    const subtitle = `${data.title || "Assessment"} · ${passed ? "passed" : "try again"}`;

    const prettyAns = (q, v) => {
        if (q == null || v == null || v === "") return "—";
        if (q.type === "mcq_single") return q.options?.[v] ?? String(v);
        if (q.type === "mcq_multi")  return (Array.isArray(v) ? v : []).map((i) => q.options?.[i] ?? i).join(", ") || "—";
        if (q.type === "true_false") return v ? "True" : "False";
        if (q.type === "likert")     return String(v);
        if (q.type === "scenario")   return Array.isArray(v) ? v.join(" → ") : String(v);
        if (q.type === "file")       return v?.name || "file";
        return String(v);
    };
    const prettyCorrect = (q) => {
        if (!q) return null;
        if (q.type === "mcq_single") return q.options?.[q.correct_answer] ?? String(q.correct_answer);
        if (q.type === "mcq_multi")  return (q.correct_answer || []).map((i) => q.options?.[i] ?? i).join(", ");
        if (q.type === "true_false") return q.correct_answer ? "True" : "False";
        if (q.type === "short")      return q.correct_answer || null;
        return null;
    };

    return (
        <div className="asm-r2-overlay" onClick={onClose} data-testid="asm-result-overlay">
            <div className={`asm-r2-shell tone-${passed ? "pass" : "fail"}`} onClick={(e) => e.stopPropagation()} data-testid="asm-result">
                <button type="button" className="asm-r2-close" onClick={onClose} aria-label="Close" data-testid="asm-result-close">
                    <X size={20} strokeWidth={2.6}/>
                </button>

                <div className="asm-r2-hero">
                    <Medallion kind={passed ? "gold" : "retry"} />
                    <h2 className="asm-r2-title" data-testid="asm-result-title">{headline}</h2>
                    <div className="asm-r2-subtitle">{subtitle}</div>
                </div>

                <div className="asm-r2-stats">
                    <div className={`asm-r2-stat tone-score ${passed ? "pass" : "fail"}`}>
                        <div className="asm-r2-stat-value">{pct}%</div>
                        <div className="asm-r2-stat-label">score</div>
                    </div>
                    <div className="asm-r2-stat tone-mark">
                        <div className="asm-r2-stat-value">{threshold}%</div>
                        <div className="asm-r2-stat-label">pass mark</div>
                    </div>
                    <div className="asm-r2-stat tone-xp">
                        <div className="asm-r2-stat-value">{passed && data.awards_xp ? "+XP" : canRetry ? "↻" : "—"}</div>
                        <div className="asm-r2-stat-label">{passed && data.awards_xp ? "earned" : canRetry ? "retry" : "—"}</div>
                    </div>
                </div>

                {certEligible && (
                    <CertificateRow
                        name={displayName}
                        assessmentTitle={data.title || "Assessment"}
                        scorePct={pct}
                        issuedAt={issuedAt}
                        attemptId={attemptId || data.id}
                        shortDate={shortDate}
                        certId={certId}
                    />
                )}

                {!certEligible && canRetry && onRetry && (
                    <div className="asm-r2-retry-row">
                        <button type="button" className="asm-r2-btn-pill primary" onClick={onRetry} data-testid="asm-result-retry">
                            <RotateCcw size={16}/> Retry assessment
                        </button>
                        <div className="asm-r2-retry-meta">
                            {(data.max_attempts === 0) ? "Unlimited attempts" : `${(data.attempts_used ?? 0)} of ${data.max_attempts} used`}
                        </div>
                    </div>
                )}

                {showFeedback && (
                    <div className="asm-r2-feedback-panel" data-testid="asm-feedback-list">
                        {Object.entries(feedback).map(([qid, fb], i) => {
                            const q = qById[qid];
                            const userAns = data.answers?.[qid];
                            const correct = prettyCorrect(q);
                            const scored = fb.points >= fb.max_points ? "pass" : fb.points > 0 ? "partial" : "fail";
                            return (
                                <div key={qid} className={`asm-r2-fb-row ${scored}`}>
                                    <div className="asm-r2-fb-rank">Q{i + 1}</div>
                                    <div className="asm-r2-fb-body">
                                        <div className="asm-r2-fb-points"><strong>{fb.points}</strong> / {fb.max_points} pts — {fb.feedback}</div>
                                        {q?.prompt && <div className="asm-r2-fb-prompt">{q.prompt}</div>}
                                        {scored !== "pass" && correct && (
                                            <div className="asm-r2-fb-correct">
                                                <div><span>Your answer:</span> {prettyAns(q, userAns)}</div>
                                                <div><span>Correct answer:</span> <strong>{correct}</strong></div>
                                            </div>
                                        )}
                                        {fb.explanation && <div className="asm-r2-fb-exp">↳ {fb.explanation}</div>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="asm-r2-footer">
                    <button
                        type="button"
                        className={`asm-r2-feedback-link ${showFeedback ? "open" : ""}`}
                        onClick={() => setShowFeedback((v) => !v)}
                        aria-expanded={showFeedback}
                        data-testid="asm-feedback-toggle"
                    >
                        {showFeedback ? "Hide feedback" : "Question feedback"}
                        <ChevronDown size={14} className="asm-r2-feedback-link-chev"/>
                    </button>
                    <button type="button" className="asm-r2-done" onClick={onClose} data-testid="asm-result-done">Done</button>
                </div>
            </div>
        </div>
    );
};

/* ── Medallion illustration ──────────────────────────────────────────── */
const Medallion = ({ kind = "gold" }) => {
    // Gold medal with red + blue ribbons, white-rimmed body, dark checkmark.
    // kind="retry": muted silver/grey version with a repeat arrow instead.
    const gold = kind === "gold";
    return (
        <div className="asm-r2-medal" aria-hidden data-testid="asm-r2-medal">
            <svg viewBox="0 0 200 220" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
                {/* Red ribbon — left streamer, slopes down to centre */}
                <path d="M46 4 L82 4 L116 108 L84 108 Z" fill={gold ? "#E54848" : "#9CA3AF"}/>
                {/* Blue ribbon — right streamer, slopes down to centre */}
                <path d="M154 4 L118 4 L84 108 L116 108 Z" fill={gold ? "#2E5EC9" : "#6B7280"}/>
                {/* Ribbon fold highlight — subtle inner shadow lines */}
                <path d="M82 4 L116 108" stroke="rgba(0,0,0,0.14)" strokeWidth="1.4"/>
                <path d="M118 4 L84 108" stroke="rgba(0,0,0,0.14)" strokeWidth="1.4"/>
                {/* Medal outer glow */}
                <circle cx="100" cy="150" r="70" fill={gold ? "#FFCD5C" : "#D4D7DC"} opacity="0.35"/>
                {/* Medal body */}
                <circle cx="100" cy="150" r="58" fill={gold ? "#F0B840" : "#B8BCC4"}/>
                {/* Medal highlight — top-left radial */}
                <ellipse cx="78" cy="130" rx="18" ry="12" fill={gold ? "#FFDE87" : "#E5E7EB"} opacity="0.65"/>
                {/* Inner white ring */}
                <circle cx="100" cy="150" r="48" fill="none" stroke="#FFFFFF" strokeWidth="6"/>
                {/* Mark */}
                {gold ? (
                    <path d="M76 152 l18 18 l32 -38" fill="none" stroke="#163B4E" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round"/>
                ) : (
                    <>
                        <path d="M124 128 a26 26 0 1 0 10 24" fill="none" stroke="#163B4E" strokeWidth="9" strokeLinecap="round"/>
                        <path d="M132 110 L124 134 L148 134" fill="none" stroke="#163B4E" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                )}
            </svg>
        </div>
    );
};

/* ── Compact Certificate row inside the result modal ─────────────────── */
const CertificateRow = ({ name, assessmentTitle, scorePct, issuedAt, attemptId, shortDate, certId }) => {
    const hiddenRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const download = async () => {
        if (!hiddenRef.current || busy) return;
        setBusy(true);
        try {
            const { default: html2canvas } = await import("html2canvas");
            // Make hidden container visible for the capture, then re-hide
            const node = hiddenRef.current;
            node.style.visibility = "visible";
            node.style.left = "0";
            node.style.top = "-9999px";
            const canvas = await html2canvas(node.querySelector(".asm-cert"), {
                backgroundColor: null,
                scale: 2,
                useCORS: true,
                logging: false,
            });
            node.style.visibility = "hidden";
            node.style.left = "-9999px";
            const url = canvas.toDataURL("image/png");
            const a = document.createElement("a");
            const safeName = (name || "trainee").replace(/[^a-z0-9]+/gi, "_");
            const safeTitle = (assessmentTitle || "assessment").replace(/[^a-z0-9]+/gi, "_");
            a.download = `Odyssey_${safeTitle}_${safeName}.png`;
            a.href = url;
            a.click();
            toast.success("Certificate downloaded");
        } catch {
            toast.error("Could not render certificate — try again");
        } finally {
            setBusy(false);
        }
    };

    const share = async () => {
        const text = `I just earned the Odyssey "${assessmentTitle}" certificate — ${scorePct}%.`;
        try {
            if (navigator.share) await navigator.share({ title: "Odyssey Certificate", text });
            else {
                await navigator.clipboard.writeText(text);
                toast.success("Share text copied to clipboard");
            }
        } catch { /* user dismissed */ }
    };

    return (
        <>
            <div className="asm-r2-cert" data-testid="asm-certificate">
                <div className="asm-r2-cert-left">
                    <div className="asm-r2-cert-caption">Certificate of Completion</div>
                    <div className="asm-r2-cert-name" data-testid="asm-cert-name">{name}</div>
                    <div className="asm-r2-cert-meta">{shortDate} · {certId}</div>
                </div>
                <div className="asm-r2-cert-actions">
                    <button type="button" className="asm-r2-btn-pill primary" onClick={download} disabled={busy} data-testid="asm-cert-download">
                        {busy ? "…" : "Download"}
                    </button>
                    <button type="button" className="asm-r2-btn-pill ghost" onClick={share} data-testid="asm-cert-share">
                        Share
                    </button>
                </div>
            </div>
            {/* Off-screen full certificate used for the PNG export */}
            <div
                ref={hiddenRef}
                style={{ position: "absolute", left: -9999, top: -9999, visibility: "hidden", pointerEvents: "none", width: 760 }}
                aria-hidden
            >
                <Certificate
                    name={name}
                    assessmentTitle={assessmentTitle}
                    scorePct={scorePct}
                    issuedAt={issuedAt}
                    attemptId={attemptId}
                />
            </div>
        </>
    );
};

/* ── Certificate component — AMC-inspired, Odyssey theme ─────────────── */
/* Rendered off-screen; its DOM is captured by html2canvas for the PNG export. */
const Certificate = ({ name, assessmentTitle, scorePct, issuedAt, attemptId }) => {
    const certId = `OD-${String(attemptId || "").slice(-6).toUpperCase() || "000000"}`;
    const dateStr = (issuedAt instanceof Date ? issuedAt : new Date(issuedAt)).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    return (
        <div className="asm-cert" data-testid="asm-certificate-full">
            <div className="asm-cert-grain" aria-hidden/>
            <div className="asm-cert-title">Certificate of Completion</div>
            <div className="asm-cert-lede">This recognises that</div>
            <div className="asm-cert-name">{name}</div>
            <div className="asm-cert-name-rule" aria-hidden/>
            <div className="asm-cert-course-lede">has successfully completed</div>
            <div className="asm-cert-course">{assessmentTitle}</div>
            <div className="asm-cert-date">On <strong>{dateStr}</strong></div>
            <div className="asm-cert-mark" aria-hidden>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4L8 10v12c0 10 7 18 16 22 9-4 16-12 16-22V10L24 4z" fill="currentColor" fillOpacity="0.14" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
                    <path d="M16.5 24.5l5.5 5.5 10-11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
            <div className="asm-cert-id">Award ID · <strong>{certId}</strong></div>
            <div className="asm-cert-footer">
                <span className="asm-cert-footer-mark">O</span>
                <span className="asm-cert-footer-text">ODYSSEY<em>| Learning Academy</em></span>
            </div>
        </div>
    );
};

export default AssessmentsPanel;
